// States the api exposes on each licence entry (tempWorker/info). The app reads them, it never
// recomputes them from the expiration date.
export const LICENCE_EXPIRY_ALERT_STATES = ["expiring", "expired"];

const normalizeCategory = (category) =>
    typeof category === "string" && category.trim() ? category.trim() : null;

const normalizeForSearch = (value) =>
    String(value || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase();

// "CACES R489 · catégorie 3", or the name alone ("Permis B"). The template comes from the
// licences.label_with_category locale key.
export const formatLicenceLabel = ({licence, category} = {}, labelWithCategoryTemplate = "{licence} · catégorie {category}") => {
    const name = typeof licence === "string" ? licence.trim() : "";
    const normalizedCategory = normalizeCategory(category);
    if (!normalizedCategory) return name;
    return labelWithCategoryTemplate.replace("{licence}", name).replace("{category}", normalizedCategory);
};

const buildOptionKey = (licence, category) => JSON.stringify([licence, category ?? ""]);

/**
 * Flattens the licences catalogue (GET licence) into one option per category when an entry has
 * some (CACES R489 → one option per category), else the entry alone. The api refuses a CACES
 * with categories sent without one, so the bare entry is never offered for those.
 * @param {Array<{name: string, categories?: string[]}>} catalogue
 * @returns {Array<{key: string, licence: string, category: string|null}>}
 */
export const buildLicenceCatalogueOptions = (catalogue) =>
    (Array.isArray(catalogue) ? catalogue : [])
        .filter((entry) => typeof entry?.name === "string" && entry.name.trim())
        .sort((a, b) => a.name.localeCompare(b.name, "fr", {sensitivity: "base", numeric: true}))
        .flatMap((entry) => {
            const categories = (Array.isArray(entry.categories) ? entry.categories : [])
                .map(normalizeCategory)
                .filter(Boolean);
            if (!categories.length) {
                return [{key: buildOptionKey(entry.name, null), licence: entry.name, category: null}];
            }
            return categories.map((category) => ({
                key: buildOptionKey(entry.name, category),
                licence: entry.name,
                category,
            }));
        });

// Every word of the query must appear in the label, accents and case aside: "r489 3" finds
// "CACES R489 · catégorie 3".
export const filterLicenceOptions = (options, query, getLabel) => {
    const words = normalizeForSearch(query).split(/\s+/).filter(Boolean);
    if (!words.length) return options;
    return options.filter((option) => {
        const label = normalizeForSearch(getLabel(option));
        return words.every((word) => label.includes(word));
    });
};

export const isSameLicence = (a, b) =>
    Boolean(a && b) &&
    normalizeForSearch(a.licence).trim() === normalizeForSearch(b.licence).trim() &&
    normalizeForSearch(normalizeCategory(a.category) ?? "") === normalizeForSearch(normalizeCategory(b.category) ?? "");

export const findLicenceOption = (options, wanted) =>
    wanted ? options.find((option) => isSameLicence(option, wanted)) || null : null;

/**
 * The licence the home banner warns about: among entries the api marks expiring or expired, the
 * one whose expiration comes first. Expiration is a calendar day at midnight UTC, so comparing
 * its ISO string is enough.
 */
export const pickLicenceExpiryAlert = (licences) =>
    (Array.isArray(licences) ? licences : [])
        .filter((entry) => LICENCE_EXPIRY_ALERT_STATES.includes(entry?.state) && typeof entry?.expiration === "string")
        .sort((a, b) => a.expiration.localeCompare(b.expiration))[0] || null;

// A CACES is a catalogue entry whose name starts with "CACES"; everything else is a driving licence.
export const getLicenceKind = ({licence} = {}) =>
    /^\s*caces\b/i.test(String(licence || "")) ? "caces" : "permit";

// Tint of the icon tile, per state computed by the api.
const LICENCE_STATE_TONES = {
    refused: "red",
    expired: "red",
    pending: "yellow",
    expiring: "yellow",
    valid: "green",
    accepted: "green",
};

export const getLicenceTone = ({state} = {}) => LICENCE_STATE_TONES[state] || "yellow";

export const fillTemplate = (template, values = {}) =>
    Object.entries(values).reduce(
        (text, [name, value]) => text.split(`{${name}}`).join(value ?? ""),
        String(template ?? "")
    );

/**
 * Line under the licence name in "Mes permis". getTemplate(key) returns the licences.list.state.<key>
 * template. A dated state without expiration falls back to "Accepté" rather than an empty date.
 */
export const getLicenceSubtitle = (entry, getTemplate, formatDate) => {
    const {state, reason, expiration} = entry || {};
    if (state === "refused") {
        return reason ? fillTemplate(getTemplate("refused"), {reason}) : getTemplate("refused_no_reason");
    }
    if (["valid", "expiring", "expired"].includes(state)) {
        const date = formatDate(expiration);
        return date ? fillTemplate(getTemplate(state), {date}) : getTemplate("accepted");
    }
    if (state === "accepted") return getTemplate("accepted");
    return getTemplate("pending");
};

// validatedAt is an instant, not a calendar day: the user's local day is the one to show.
export const formatInstantDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${date.getFullYear()}`;
};
